import { parsePorcoes } from "@/lib/utils";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { Product } from "@workspace/api-client-react/src/generated/api.schemas";
import { getActivePromotion, gruposComPromocao, quantityPromoDiscounts } from "@/lib/promotion";
import {
  computeItemTotal,
  computeOptionsTotal,
  round2,
  pickAndPriceOptions,
  isValidGroupSelection,
  selectionsByGroupIndex,
  validateRemovedIngredients,
  modoDaEncomenda,
  turnosDoProduto,
  groupsForValidation,
  prepCountDoItem,
  prepCountFor,
  prepCountOn,
} from "@workspace/pricing";

// Valor das opções da unidade com os extras de preparo (contar preparos, 15/10/2026): o mesmo que a tela do produto
// mostrou e o servidor cobra. Sem a contagem (ou sem o recurso, que a vitrine já tira), é o valor das opções de sempre.
// Promoção em % no produto de preço base 0 (23/10/2026): as opções que formam o preço com o desconto, como o servidor.
function optionsTotalDoItem(product: unknown, selected: Array<{ groupName: string; optionName: string; price: number; groupIndex?: number | null }> | undefined): number {
  const groups = gruposComPromocao(product, "link");
  const prep = prepCountFor((product as any)?.optionGroups, (product as any)?.prepCount, selected);
  return round2(computeOptionsTotal(groups, selected) + (prep?.extrasValue ?? 0));
}

export interface SelectedOption {
  groupName: string;
  optionName: string;
  price: number;
  /** Posição do grupo em optionGroups — desempata grupos de mesmo nome. */
  groupIndex?: number;
}

export interface CartItem {
  product: Product;
  quantity: number;
  notes?: string;
  cartItemId: string;
  selectedOptions?: SelectedOption[];
  removedIngredients?: string[];
  optionsTotal: number;
  scheduledDate?: string;
  scheduledTime?: string;
  /** Turno da encomenda ('manha' | 'tarde' | 'noite') — produto que pede turno (12/10/2026). */
  scheduledShift?: string;
  unitPrice: number;
  originalUnitPrice: number;
  discountAmount: number;
  /** Porção escolhida (kg) — produto por peso vendido no link. */
  weightKg?: number;
}

function computePriceSnapshot(product: Product, weightKg?: number) {
  const promo = getActivePromotion(product, new Date(), "link");
  if (weightKg != null && weightKg > 0) {
    // Por peso: porção com preço FIXADO vence (palavra do lojista); senão
    // peso × R$/kg (promo por kg respeitada). Espelho do servidor.
    const fixo = parsePorcoes((product as any).portions).find((o) => Math.abs(o.kg - weightKg) < 0.0005)?.price;
    const perKg = promo.active ? promo.finalPrice : promo.originalPrice;
    const unit = fixo != null && fixo > 0 ? fixo : Math.round(perKg * weightKg * 100) / 100;
    return { unitPrice: unit, originalUnitPrice: unit, discountAmount: 0 };
  }
  return {
    unitPrice: promo.active ? promo.finalPrice : promo.originalPrice,
    originalUnitPrice: promo.originalPrice,
    discountAmount: promo.active ? promo.discountAmount : 0,
  };
}

/** Desconto por quantidade no carrinho do LINK — fórmula única em
 * @/lib/promotion (a mesma da divisão de linhas no servidor). */
export function quantityPromoCartDiscounts(items: CartItem[]) {
  return quantityPromoDiscounts(items, "link");
}

/** Item de um pedido já feito, como `GET /api/catalog/:slug/orders` devolve. */
export interface LinhaDoPedidoAntigo {
  productId: string | null;
  productName: string;
  quantity: number;
  notes?: string | null;
  selectedOptions?: Array<{ groupName: string; optionName: string; price: number | string; groupIndex?: number | null }> | null;
  removedIngredients?: string[] | null;
  weightKg?: number | string | null;
}

interface CartStore {
  items: CartItem[];
  addItem: (product: Product, quantity?: number, notes?: string, selectedOptions?: SelectedOption[], removedIngredients?: string[], scheduledDate?: string, scheduledTime?: string, weightKg?: number, scheduledShift?: string) => void;
  removeItem: (cartItemId: string) => void;
  updateItem: (cartItemId: string, quantity: number, notes: string, selectedOptions?: SelectedOption[], removedIngredients?: string[], scheduledDate?: string, scheduledTime?: string, weightKg?: number, scheduledShift?: string) => void;
  updateQuantity: (cartItemId: string, quantity: number) => void;
  updateNotes: (cartItemId: string, notes: string) => void;
  clearCart: () => void;
  /** Reidrata o carrinho salvo contra o catálogo recém-lido. Devolve quantas
   *  linhas caíram. Ver `syncWithCatalog` na implementação. */
  syncWithCatalog: (products: Product[]) => number;
  /** «Pedir de novo»: põe no carrinho as linhas de um pedido antigo, refeitas pelo catálogo de agora (preço de hoje).
   *  Devolve quantas entraram e o nome das que ficaram de fora (saiu do cardápio, esgotou, mudou ou é encomenda). */
  pedirDeNovo: (linhas: LinhaDoPedidoAntigo[], products: Product[]) => { postas: number; fora: string[] };
  getTotal: () => number;
  getItemCount: () => number;
}

function generateCartItemId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

// Assinatura do que o cliente montou. Duas adições com a MESMA assinatura são
// a mesma linha do carrinho e viram quantidade — o cupom sai "4x Enroladinho"
// em vez de quatro linhas "1x" (relato da Pastelaria São Sebastião, 30/07/2026).
// Só o que o cliente vê entra aqui: adicionais, ingredientes retirados,
// observação e agendamento. Adicional diferente (ou observação diferente)
// continua sendo linha separada, senão a cozinha perderia a instrução.
function lineSignature(
  productId: string,
  notes: string | undefined,
  selectedOptions: SelectedOption[] | undefined,
  removedIngredients: string[] | undefined,
  scheduledDate: string | undefined,
  scheduledTime: string | undefined,
  weightKg?: number,
  scheduledShift?: string,
): string {
  // Separador escrito como ESCAPE, nunca byte NUL cru no fonte: byte cru faz
  // git e editores tratarem o arquivo como binario (mesmo cuidado do KEY_SEP
  // em api-server/src/lib/optionStock.ts). Espaco nao serviria de separador:
  // nome de adicional tem espaco, e "A B"+"C" colidiria com "A"+"B C".
  const SEP = "\u0000";
  const opts = (selectedOptions || [])
    .map((o) => [o.groupIndex ?? "", o.groupName, o.optionName].join(SEP))
    .sort();
  const removed = [...(removedIngredients || [])].sort();
  return JSON.stringify([
    productId,
    (notes || "").trim(),
    opts,
    removed,
    scheduledDate || "",
    scheduledTime || "",
    weightKg ?? "",
    scheduledShift || "",
  ]);
}

function signatureOf(item: CartItem): string {
  return lineSignature(
    item.product.id,
    item.notes,
    item.selectedOptions,
    item.removedIngredients,
    item.scheduledDate,
    item.scheduledTime,
    item.weightKg,
    item.scheduledShift,
  );
}

// Limite de quantidade por carrinho do produto (null = sem limite).
function maxPerCartOf(product: Product): number | null {
  const v = (product as any).maxPerCart;
  return typeof v === "number" && v > 0 ? v : null;
}

// Teto de quantidade do ITEM imposto pelo estoque das opções PERSONALIZADAS
// selecionadas: cada unidade do item consome (nº de vezes que a opção aparece
// na seleção) unidades do estoque da opção. Ex: opção "M" com stock 2
// selecionada 1x → máx. 2 itens. null = sem teto. Estoque de INGREDIENTE não
// entra aqui (mora no registry da loja, não no snapshot do produto) — pra ele
// o servidor segue sendo a barreira, como antes.
export function optionStockCap(
  product: Product,
  selectedOptions?: SelectedOption[] | null,
): number | null {
  if (!selectedOptions || selectedOptions.length === 0) return null;
  const groups = ((product as any).optionGroups || []) as any[];
  const usage = new Map<string, { stock: number; count: number }>();
  for (const sel of selectedOptions) {
    // Grupo pelo índice quando veio (nome repete entre grupos); senão o
    // primeiro homônimo que TEM a opção.
    const gi =
      typeof sel.groupIndex === "number" && groups[sel.groupIndex]?.name === sel.groupName
        ? sel.groupIndex
        : groups.findIndex(
            (gr) =>
              gr?.name === sel.groupName &&
              (gr?.options || []).some((op: any) => op?.name === sel.optionName),
          );
    const o = gi >= 0 ? groups[gi]?.options?.find((op: any) => op?.name === sel.optionName) : undefined;
    if (!o || o.ingredientId || o.stock === null || o.stock === undefined) continue;
    const key = JSON.stringify([gi, sel.optionName]);
    const cur = usage.get(key);
    if (cur) cur.count += 1;
    else usage.set(key, { stock: Number(o.stock) || 0, count: 1 });
  }
  let cap: number | null = null;
  for (const { stock, count } of usage.values()) {
    const c = Math.floor(stock / count);
    cap = cap === null ? c : Math.min(cap, c);
  }
  return cap;
}

// Soma da quantidade desse produto em OUTRAS linhas do carrinho (o mesmo
// produto pode ter várias linhas quando tem opções). Exclui a linha informada.
function otherProductQty(items: CartItem[], productId: string, excludeCartItemId?: string): number {
  return items.reduce(
    (s, i) => (i.product.id === productId && i.cartItemId !== excludeCartItemId ? s + i.quantity : s),
    0,
  );
}

const cartStoreCache = new Map<string, ReturnType<typeof buildCartStore>>();

export function getCartStore(storeSlug: string) {
  let store = cartStoreCache.get(storeSlug);
  if (!store) {
    store = buildCartStore(storeSlug);
    cartStoreCache.set(storeSlug, store);
  }
  return store;
}

export const createCartStore = getCartStore;

// Uma linha do carrinho refeita pelo catálogo de agora (preço, promoção, adicionais e ingredientes); null = não fecha
// mais e cai. Serve à reidratação do carrinho salvo e ao «Pedir de novo».
function reconstruirLinha(item: CartItem, p: Product): CartItem | null {
  const status = (p as any)?.status;
  const estoque = (p as any)?.stock;
  if ((status && status !== "ativo") || (estoque !== null && estoque !== undefined && Number(estoque) <= 0)) {
    return null;
  }
  // Linha por porção: a porção precisa continuar cadastrada (e o
  // produto continuar por peso). Saldo em kg zerado derruba também.
  if (item.weightKg != null) {
    const porcoes = parsePorcoes((p as any).portions);
    const kgSaldo = (p as any).stockKg != null ? Number((p as any).stockKg) : null;
    // Com porções, o peso precisa continuar cadastrado; fracionado
    // aceita qualquer peso > 0 (o servidor revalida a faixa).
    const pesoOk = porcoes.length > 0
      ? porcoes.some((o) => Math.abs(o.kg - item.weightKg!) < 0.0005)
      : item.weightKg > 0;
    if (!(p as any).soldByWeight || !pesoOk || (kgSaldo !== null && kgSaldo <= 0)) {
      return null;
    }
  }
  // Repreça pelo cardápio de agora, com a promoção em % nas opções quando vale (o servidor cobra assim).
  const groups = gruposComPromocao(p, "link");
  const { validated, optionsTotal: optionsDoCardapio, unmatched } = pickAndPriceOptions(
    groups,
    item.selectedOptions ?? [],
  );
  // Pacote de escolhas: o grupo não tem máximo próprio, e a linha cai se ficou fora de N..M (config mudou).
  const contando = prepCountOn((p as any).prepCount);
  const preparos = contando ? prepCountDoItem((p as any).prepCount, groups, validated) : null;
  const optionsTotal = round2(optionsDoCardapio + (preparos?.extrasValue ?? 0));
  const gruposDaRegra = groupsForValidation(groups, contando);
  // Adicional que sumiu do cardápio, ou regra do grupo que endureceu
  // depois que o carrinho foi salvo (mínimo novo, teto menor).
  if (
    unmatched.length > 0 ||
    !isValidGroupSelection(gruposDaRegra, selectionsByGroupIndex(gruposDaRegra, validated)) ||
    (preparos != null && (preparos.missing > 0 || preparos.overMax))
  ) {
    return null;
  }
  // Encomenda guardada com uma escolha que o produto não pede mais (o lojista trocou horário/turno/data
  // ou desligou o turno): a linha cai e a vitrine avisa, como a do adicional que sumiu.
  if (item.scheduledDate && (p as any).isOrderBased) {
    const modo = modoDaEncomenda(p as any);
    const semHorario = modo === "horario" && !item.scheduledTime;
    const semTurno = modo === "turno" && !item.scheduledTime && (!item.scheduledShift || !turnosDoProduto(p as any).includes(item.scheduledShift as any));
    if (semHorario || semTurno) {
      return null;
    }
  }
  const removidos = validateRemovedIngredients(
    (p as any).removableIngredients,
    item.removedIngredients,
  );
  return {
    ...item,
    product: p,
    selectedOptions: validated.length > 0 ? (validated as SelectedOption[]) : undefined,
    optionsTotal,
    removedIngredients: removidos.length > 0 ? removidos : undefined,
    ...computePriceSnapshot(p, item.weightKg),
  };
}

const buildCartStore = (storeSlug: string) => {
  return create<CartStore>()(
    persist(
      (set, get) => ({
        items: [],
        addItem: (product, quantity = 1, notes = "", selectedOptions, removedIngredients, scheduledDate, scheduledTime, weightKg, scheduledShift) => set((state) => {
          const cap = (qty: number, stock: number | null) => stock === null ? qty : Math.min(qty, stock);
          const maxPer = maxPerCartOf(product);
          const optionsTotal = optionsTotalDoItem(product, selectedOptions);
          const snapshot = computePriceSnapshot(product, weightKg);

          // Antes a fusão só valia pra produto SEM nenhuma personalização
          // possível (`hasOptions`), o que testava a capacidade do produto e não
          // o que o cliente de fato escolheu: um salgado com ingrediente
          // removível virava uma linha nova a cada clique mesmo sem o cliente
          // tirar nada. Agora o que decide é a assinatura da linha.
          const sig = lineSignature(product.id, notes, selectedOptions, removedIngredients, scheduledDate, scheduledTime, weightKg, scheduledShift);
          const existing = state.items.find(item => signatureOf(item) === sig);
          if (existing) {
            let nextQty = cap(existing.quantity + quantity, product.stock);
            if (maxPer !== null) {
              nextQty = Math.min(nextQty, maxPer - otherProductQty(state.items, product.id, existing.cartItemId));
            }
            const optCapMerge = optionStockCap(product, selectedOptions);
            if (optCapMerge !== null) nextQty = Math.min(nextQty, optCapMerge);
            if (nextQty <= 0) return state;
            return {
              items: state.items.map(item =>
                item.cartItemId === existing.cartItemId
                  ? { ...item, quantity: nextQty }
                  : item
              )
            };
          }

          let newQty = cap(quantity, product.stock);
          if (maxPer !== null) {
            newQty = Math.min(newQty, maxPer - otherProductQty(state.items, product.id));
          }
          const optCapAdd = optionStockCap(product, selectedOptions);
          if (optCapAdd !== null) newQty = Math.min(newQty, optCapAdd);
          if (newQty <= 0) return state;

          const newItem: CartItem = {
            product,
            quantity: newQty,
            notes,
            cartItemId: generateCartItemId(),
            selectedOptions: selectedOptions && selectedOptions.length > 0 ? selectedOptions : undefined,
            removedIngredients: removedIngredients && removedIngredients.length > 0 ? removedIngredients : undefined,
            optionsTotal,
            scheduledDate,
            scheduledTime,
            scheduledShift,
            weightKg,
            ...snapshot,
          };
          return { items: [...state.items, newItem] };
        }),
        removeItem: (cartItemId) => set((state) => ({
          items: state.items.filter(item => item.cartItemId !== cartItemId)
        })),
        updateItem: (cartItemId, quantity, notes, selectedOptions, removedIngredients, scheduledDate, scheduledTime, weightKg, scheduledShift) => set((state) => {
          return {
            items: state.items.map(item => {
              if (item.cartItemId !== cartItemId) return item;
              const optionsTotal = optionsTotalDoItem(item.product, selectedOptions);
              const novaPorcao = weightKg !== undefined && weightKg !== item.weightKg;
              const snapshot = !novaPorcao && item.unitPrice !== undefined
                ? { unitPrice: item.unitPrice, originalUnitPrice: item.originalUnitPrice, discountAmount: item.discountAmount }
                : computePriceSnapshot(item.product, weightKg ?? item.weightKg);
              const maxPer = maxPerCartOf(item.product);
              let q = item.product.stock === null ? quantity : Math.min(quantity, item.product.stock);
              if (maxPer !== null) q = Math.min(q, maxPer - otherProductQty(state.items, item.product.id, cartItemId));
              const optCapUpd = optionStockCap(item.product, selectedOptions);
              if (optCapUpd !== null) q = Math.min(q, optCapUpd);
              return {
                ...item,
                quantity: q,
                notes,
                selectedOptions: selectedOptions && selectedOptions.length > 0 ? selectedOptions : undefined,
                removedIngredients: removedIngredients && removedIngredients.length > 0 ? removedIngredients : undefined,
                optionsTotal,
                scheduledDate,
                scheduledTime,
                scheduledShift,
                weightKg: weightKg !== undefined ? weightKg : item.weightKg,
                ...snapshot,
              };
            })
          };
        }),
        updateQuantity: (cartItemId, quantity) => set((state) => {
          if (quantity <= 0) {
            return { items: state.items.filter(item => item.cartItemId !== cartItemId) };
          }
          return {
            items: state.items.map(item => {
              if (item.cartItemId !== cartItemId) return item;
              let q = item.product.stock === null ? quantity : Math.min(quantity, item.product.stock);
              const maxPer = maxPerCartOf(item.product);
              if (maxPer !== null) q = Math.min(q, maxPer - otherProductQty(state.items, item.product.id, cartItemId));
              // Estoque das opções selecionadas também limita o "+" do carrinho
              // (ex: só 2 "M" em estoque → item com M para em 2 unidades).
              const optCap = optionStockCap(item.product, item.selectedOptions);
              if (optCap !== null) q = Math.min(q, optCap);
              return { ...item, quantity: q };
            })
          };
        }),
        updateNotes: (cartItemId, notes) => set((state) => ({
          items: state.items.map(item =>
            item.cartItemId === cartItemId ? { ...item, notes } : item
          )
        })),
        clearCart: () => set({ items: [] }),
        // ⚠️ O carrinho persiste um RETRATO (produto, preço, nomes) sem prazo
        // de validade. Sem isto, quem reabre a aba amanhã vê o preço de ontem
        // e o servidor cobra o de hoje — a tela mostra um valor e o pedido sai
        // com outro (mesma família do incidente da taxa de entrega). Aqui cada
        // linha é reconstruída do catálogo que acabou de chegar: preço e
        // promoção viram os de agora, adicionais e ingredientes são
        // reresolvidos pelo cardápio atual (com fold de caixa/acento) e a
        // linha que não fecha mais é DESCARTADA — a vitrine avisa quantas.
        syncWithCatalog: (products) => {
          const atual = new Map(products.map((p) => [p.id, p]));
          const antes = get().items;
          if (antes.length === 0) return 0;
          let caidas = 0;
          const next: CartItem[] = [];
          for (const item of antes) {
            const p = atual.get(item.product.id);
            const refeita = p ? reconstruirLinha(item, p) : null;
            if (refeita) next.push(refeita);
            else caidas++;
          }
          const assinatura = (list: CartItem[]) =>
            list
              .map((i) => [i.cartItemId, i.unitPrice, i.optionsTotal, (i.selectedOptions || []).map((o) => o.optionName).join(","), (i.removedIngredients || []).join(",")].join("|"))
              .join(";");
          if (assinatura(antes) !== assinatura(next)) set({ items: next });
          return caidas;
        },
        pedirDeNovo: (linhas, products) => {
          const atual = new Map(products.map((p) => [p.id, p]));
          let postas = 0;
          const fora: string[] = [];
          for (const l of linhas) {
            const p = l.productId ? atual.get(l.productId) : undefined;
            const opcoes = (l.selectedOptions ?? []).map((o) => ({
              groupName: o.groupName,
              optionName: o.optionName,
              price: Number(o.price) || 0,
              ...(o.groupIndex != null ? { groupIndex: o.groupIndex } : {}),
            }));
            // «Marmita (300g)»: com o nome só, a linha de fora se confunde com a do mesmo produto que entrou.
            const nome = opcoes.length > 0 ? `${l.productName} (${opcoes.map((o) => o.optionName).join(", ")})` : l.productName;
            // Encomenda pede data e horário de novo: a do pedido antigo já passou, o cliente escolhe no produto.
            if (!p || (p as any).isOrderBased) {
              fora.push(nome);
              continue;
            }
            const refeita = reconstruirLinha(
              {
                product: p,
                quantity: Math.max(1, Math.floor(Number(l.quantity) || 1)),
                notes: l.notes ?? "",
                cartItemId: "",
                selectedOptions: opcoes.length > 0 ? opcoes : undefined,
                removedIngredients: l.removedIngredients?.length ? l.removedIngredients : undefined,
                weightKg: l.weightKg != null ? Number(l.weightKg) : undefined,
              } as CartItem,
              p,
            );
            if (!refeita) {
              fora.push(nome);
              continue;
            }
            const antes = get().getItemCount();
            get().addItem(refeita.product, refeita.quantity, refeita.notes, refeita.selectedOptions, refeita.removedIngredients, undefined, undefined, refeita.weightKg);
            // Teto do carrinho ou do estoque já cheio: não coube nada.
            if (get().getItemCount() > antes) postas++;
            else fora.push(nome);
          }
          return { postas, fora };
        },
        getTotal: () => {
          const { items } = get();
          const gross = items.reduce((total, item) => {
            const unit = item.unitPrice !== undefined ? item.unitPrice : Number(item.product.price);
            return total + computeItemTotal({
              unitPrice: unit,
              optionsTotal: item.optionsTotal || 0,
              quantity: item.quantity,
            });
          }, 0);
          // Promo por quantidade: desconto no total, MESMA conta do servidor —
          // sem isso o total do misto para de bater e o pedido é rejeitado.
          const qtyDiscount = quantityPromoCartDiscounts(items)
            .reduce((s, e) => s + e.discount, 0);
          return round2(Math.max(0, gross - qtyDiscount));
        },
        getItemCount: () => {
          const { items } = get();
          return items.reduce((count, item) => count + item.quantity, 0);
        }
      }),
      {
        name: `venda-no-zap-cart-${storeSlug}`,
      }
    )
  );
};
